<template>
  <div class="family-request family-page bg-slate-50">
    <header
      class="family-page-header bg-white/90 backdrop-blur-md border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          class="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-500"
          aria-label="返回"
          @click="goBack"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 class="text-base font-bold text-slate-900">发起陪护需求</h1>
          <p class="text-xs text-slate-400">选择医院、护工与可预约时间</p>
        </div>
      </div>
    </header>

    <main class="family-page-body px-4 pt-4" :style="{ paddingBottom: mainBottomPad }">
      <form @submit.prevent="handleSubmit">
        <div class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)]">
          <h3 class="text-sm font-bold text-slate-800 mb-3">医院与护工</h3>
          <label class="block mb-3">
            <span class="text-sm text-slate-600">看护医院 <span class="text-red-500">*</span></span>
            <select v-model="form.hospital" required class="family-field" @change="onHospitalChange">
              <option value="">请选择医院/院区</option>
              <option v-for="item in hospitalOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </option>
            </select>
          </label>
          <div class="block mb-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm text-slate-600">护工安排 <span class="text-red-500">*</span></span>
            </div>
            <div class="assign-mode-row mb-3">
              <button
                type="button"
                class="assign-mode-btn"
                :class="{ 'assign-mode-active': form.assignMode === 'random' }"
                @click="switchAssignMode('random')"
              >
                <span class="assign-mode-icon">🎲</span>
                <span class="assign-mode-title">随机分配</span>
                <span class="assign-mode-desc">由调度员安排合适护工</span>
              </button>
              <button
                type="button"
                class="assign-mode-btn"
                :class="{ 'assign-mode-active': form.assignMode === 'preferred' }"
                disabled
              >
                <span class="assign-mode-icon">👤</span>
                <span class="assign-mode-title">指定护工</span>
                <span class="assign-mode-desc">暂不可用</span>
              </button>
            </div>
            <p v-if="form.assignMode === 'random'" class="random-tip">
              已选择随机分配，提交后调度员将根据医院与排班为您安排护工并致电确认。
            </p>
            <template v-else>
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm text-slate-600">选择意向护工</span>
                <span v-if="form.hospital" class="text-[11px] text-slate-400">左右滑动 · 点击查看详情</span>
              </div>
              <div v-if="!form.hospital" class="text-xs text-slate-400 py-6 text-center bg-slate-50 rounded-lg">
                请先选择医院
              </div>
              <div v-else-if="caregiversLoading" class="text-xs text-slate-400 py-6 text-center">加载护工中...</div>
              <div v-else-if="caregiverOptions.length === 0" class="text-xs text-amber-600 py-4 text-center bg-amber-50 rounded-lg px-3">
                当前医院范围暂无可预约护工，建议选择随机分配
              </div>
              <div v-else class="caregiver-scroll -mx-1 px-1 pb-1">
              <article
                v-for="cg in caregiverOptions"
                :key="cg.id"
                class="caregiver-card"
                :class="{ 'caregiver-card-selected': isCaregiverSelected(cg.id) }"
                @click="openCaregiverDetail(cg)"
              >
                <div class="flex items-start gap-3">
                  <div class="caregiver-avatar-wrap">
                    <img v-if="cg.avatar" :src="resolveUrl(cg.avatar)" class="caregiver-photo" alt="" />
                    <div
                      v-else
                      class="caregiver-avatar bg-gradient-to-br"
                      :class="cg.avatarBg"
                    >
                      {{ cg.name.charAt(0) }}
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-1.5">
                      <h4 class="text-sm font-bold text-slate-900 truncate">{{ cg.name }}</h4>
                      <span class="level-tag" :class="cg.levelColor">{{ cg.level }}</span>
                    </div>
                    <p class="text-[11px] text-slate-400 mt-0.5 truncate">{{ cg.hospital }} · {{ cg.experience }}经验</p>
                    <div class="flex items-center gap-2 mt-1.5">
                      <div class="flex items-center gap-0.5">
                        <svg class="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        <span class="text-xs font-bold text-amber-600">{{ cg.rating }}</span>
                      </div>
                      <span class="text-[10px] text-slate-300">|</span>
                      <span class="text-[11px] text-slate-500">{{ cg.orders || 0 }} 单</span>
                      <span
                        class="text-[10px] px-1.5 py-0.5 rounded-full"
                        :class="cg.status === 'idle' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-500'"
                      >
                        {{ cg.statusLabel }}
                      </span>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-slate-500 mt-2 leading-relaxed line-clamp-2">{{ cg.intro }}</p>
                <div class="flex flex-wrap gap-1 mt-2">
                  <span v-for="tag in (cg.tags || []).slice(0, 3)" :key="tag" class="skill-tag">{{ tag }}</span>
                </div>
                <p v-if="isCaregiverSelected(cg.id)" class="selected-hint">已选择意向护工</p>
              </article>
            </div>
            </template>
          </div>
          <label class="block mb-3">
            <span class="text-sm text-slate-600">具体科室 <span class="text-red-500">*</span></span>
            <input v-model.trim="form.department" type="text" required placeholder="如：心内科 3 楼" class="family-field" />
          </label>
          <label class="block">
            <span class="text-sm text-slate-600">床位号 <span class="text-red-500">*</span></span>
            <input v-model.trim="form.bedNo" type="text" required placeholder="如：12 号床" class="family-field" />
          </label>
        </div>

        <div class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)]">
          <h3 class="text-sm font-bold text-slate-800 mb-3">患者情况</h3>
          <div class="flex gap-2 mb-3">
            <button
              type="button"
              class="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
              :class="patientInputMode === 'archive' ? 'bg-family-blue text-white' : 'bg-slate-100 text-slate-600'"
              @click="patientInputMode = 'archive'"
            >
              选择档案
            </button>
            <button
              type="button"
              class="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
              :class="patientInputMode === 'manual' ? 'bg-family-blue text-white' : 'bg-slate-100 text-slate-600'"
              @click="patientInputMode = 'manual'"
            >
              手动填写
            </button>
          </div>
          <div v-if="patientInputMode === 'archive'" class="mb-3">
            <span class="text-sm text-slate-600">陪护人档案</span>
            <select v-model="selectedPatientId" class="family-field mt-1.5" @change="applyPatientArchive">
              <option value="">请选择已有档案</option>
              <option v-for="p in patientArchives" :key="p.id" :value="String(p.id)">
                {{ p.name }} · {{ p.genderLabel || "" }} {{ p.age != null ? p.age + "岁" : "" }}
              </option>
            </select>
            <button
              type="button"
              class="mt-2 text-xs text-family-blue"
              @click="$router.push({ path: '/family/patients', query: { fromTab: 'home' } })"
            >
              管理陪护人档案 →
            </button>
          </div>
          <div class="flex flex-col sm:flex-row gap-3 mb-3">
            <label class="flex-1 block min-w-0">
              <span class="text-sm text-slate-600">姓名 <span class="text-red-500">*</span></span>
              <input v-model.trim="form.patientName" type="text" required placeholder="患者姓名" class="family-field" />
            </label>
            <label class="flex-1 block min-w-0 sm:max-w-[120px]">
              <span class="text-sm text-slate-600">年龄 <span class="text-red-500">*</span></span>
              <input v-model.trim="form.patientAge" type="number" min="0" max="120" required placeholder="岁" class="family-field" />
            </label>
          </div>
          <div class="mb-3">
            <span class="text-sm text-slate-600">性别</span>
            <div class="flex gap-2 mt-1.5">
              <button
                v-for="g in genders"
                :key="g.value"
                type="button"
                class="flex-1 min-w-0 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200"
                :class="form.gender === g.value ? 'bg-family-blue text-white' : 'bg-slate-100 text-slate-600'"
                @click="form.gender = g.value"
              >
                {{ g.label }}
              </button>
            </div>
          </div>
          <div>
            <span class="text-sm text-slate-600">自理能力</span>
            <div class="flex gap-1.5 mt-1.5">
              <button
                v-for="c in careLevels"
                :key="c.value"
                type="button"
                class="flex-1 min-w-0 py-2.5 px-0.5 rounded-lg text-[11px] sm:text-xs font-medium whitespace-nowrap transition-all duration-200"
                :class="form.careLevel === c.value ? 'bg-caregiver-green text-white' : 'bg-slate-100 text-slate-600'"
                @click="form.careLevel = c.value"
              >
                {{ c.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)]">
          <h3 class="text-sm font-bold text-slate-800 mb-3">看护时间</h3>
          <div v-if="canPickDates" class="mb-3">
            <p class="text-sm text-slate-600 mb-1.5">
              {{ form.assignMode === 'random' ? '未来30天可选日期' : '未来30天可预约情况' }}
            </p>
            <div v-if="form.assignMode === 'preferred'" class="legend-row">
              <span class="legend legend-ok">可预约</span>
              <span class="legend legend-busy">已有订单</span>
              <span class="legend legend-off">排班休息</span>
            </div>
            <div v-if="datesLoading" class="text-xs text-slate-400 py-4 text-center">加载可预约日期...</div>
            <div v-else>
              <div class="date-range-toolbar">
                <p class="date-range-hint">{{ selectedRangeLabel }}</p>
                <button
                  v-if="form.startDate"
                  type="button"
                  class="date-clear-btn"
                  @click="clearDateSelection"
                >
                  清除
                </button>
              </div>
              <div class="date-grid">
              <button
                v-for="item in activeDateOptions"
                :key="item.date"
                type="button"
                :disabled="!item.available"
                class="date-chip"
                :class="dateChipClass(item)"
                :title="item.available ? item.date : item.reason"
                @click="pickDate(item)"
              >
                <span class="date-chip-day">{{ item.date.slice(8) }}</span>
                <span class="date-chip-month">{{ item.date.slice(5, 7) }}月</span>
              </button>
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-slate-400 mb-3">
            {{ form.assignMode === 'preferred' ? '请先选择意向护工，再选择服务日期' : '请先选择医院后再选择服务日期' }}
          </p>
          <div class="flex flex-col sm:flex-row gap-3">
            <label class="flex-1 block min-w-0">
              <span class="text-sm text-slate-600">开始日期 <span class="text-red-500">*</span></span>
              <select v-model="form.startDate" class="family-field" :disabled="!availableDateValues.length">
                <option value="">请选择开始日期</option>
                <option v-for="date in availableDateValues" :key="'s-' + date" :value="date">{{ date }}</option>
              </select>
            </label>
            <label class="flex-1 block min-w-0">
              <span class="text-sm text-slate-600">结束日期 <span class="text-red-500">*</span></span>
              <select v-model="form.endDate" class="family-field" :disabled="!endDateValues.length">
                <option value="">请选择结束日期</option>
                <option v-for="date in endDateValues" :key="'e-' + date" :value="date">{{ date }}</option>
              </select>
            </label>
          </div>
        </div>

        <div class="flex items-start gap-2.5 rounded-xl bg-teal-50/80 border border-teal-200/60 px-3.5 py-3 mb-4">
          <svg class="flex-shrink-0 w-4 h-4 mt-0.5 text-caregiver-green" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p class="text-xs text-teal-800/90 leading-relaxed">
            提交后订单状态为「待派单」，调度员将致电确认。指定护工仅为意向，最终以管理端指派为准。
          </p>
        </div>
      </form>
    </main>

    <div
      class="fixed left-0 right-0 z-30 px-4 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent pt-3"
      :style="{ bottom: submitBarBottom }"
    >
      <button
        type="button"
        class="w-full py-3.5 rounded-xl text-white text-sm font-semibold bg-family-blue shadow-[0_6px_20px_rgb(0,130,212,0.35)] transition-all duration-200 active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
        :disabled="submitting"
        @click="handleSubmit"
      >
        <svg v-if="submitting" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ submitting ? "提交中..." : "提交陪护需求" }}
      </button>
    </div>

    <transition name="fade-modal">
      <div
        v-if="showCaregiverDetail && detailCaregiver"
        class="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
        @click.self="closeCaregiverDetail"
      >
        <div class="caregiver-sheet w-full max-w-lg bg-white rounded-t-2xl p-5 shadow-2xl">
          <div class="sheet-handle" />
          <div class="flex items-start gap-4">
            <div class="caregiver-avatar-wrap sheet-avatar">
              <img v-if="detailCaregiver.avatar" :src="resolveUrl(detailCaregiver.avatar)" class="caregiver-photo" alt="" />
              <div
                v-else
                class="caregiver-avatar bg-gradient-to-br text-2xl"
                :class="detailCaregiver.avatarBg"
              >
                {{ detailCaregiver.name.charAt(0) }}
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <h3 class="text-lg font-bold text-slate-900">{{ detailCaregiver.name }}</h3>
                <span class="level-tag text-xs" :class="detailCaregiver.levelColor">{{ detailCaregiver.level }}</span>
              </div>
              <p class="text-sm text-slate-500 mt-1">{{ detailCaregiver.hospital }} · {{ detailCaregiver.experience }}经验</p>
              <div class="flex items-center gap-3 mt-2">
                <span class="text-sm text-amber-600 font-semibold">★ {{ detailCaregiver.rating }}</span>
                <span class="text-sm text-slate-500">{{ detailCaregiver.orders || 0 }} 单服务</span>
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="detailCaregiver.status === 'idle' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-500'"
                >
                  {{ detailCaregiver.statusLabel }}
                </span>
              </div>
            </div>
          </div>
          <p class="text-sm text-slate-600 leading-relaxed mt-4">{{ detailCaregiver.intro }}</p>
          <div class="flex flex-wrap gap-1.5 mt-3">
            <span v-for="tag in detailCaregiver.tags || []" :key="tag" class="skill-tag">{{ tag }}</span>
          </div>
          <button
            type="button"
            class="mt-5 w-full py-3 rounded-xl bg-family-blue text-white text-sm font-semibold"
            @click="selectCaregiver(detailCaregiver)"
          >
            {{ isCaregiverSelected(detailCaregiver.id) ? "已选择，继续选时间" : "选择该护工" }}
          </button>
        </div>
      </div>
    </transition>

    <transition name="fade-modal">
      <div
        v-if="showSuccess"
        class="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/40 backdrop-blur-sm"
        @click.self="closeSuccess"
      >
        <div class="success-modal bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-2xl">
          <div class="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-slate-800">提交成功</h3>
          <p class="text-sm text-slate-500 mt-2 leading-relaxed">
            需求已提交，订单为待派单状态。调度员将在 15 分钟内致电确认护工安排，请保持手机畅通。
          </p>
          <button
            type="button"
            class="mt-5 w-full py-3 rounded-xl bg-family-blue text-white text-sm font-medium"
            @click="goOrdersAfterSuccess"
          >
            查看我的订单
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { familyOrderApi } from "./familyOrderApi";
import { familyPatientApi } from "./familyPatientApi";
import { buildFamilyTabQuery, familyGoBack } from "./familyNavigation";

export default {
  name: "FamilyRequest",
  data() {
    return {
      submitting: false,
      showSuccess: false,
      showCaregiverDetail: false,
      detailCaregiver: null,
      caregiversLoading: false,
      datesLoading: false,
      hospitalOptions: [],
      caregiverOptions: [],
      dateOptions: [],
      patientInputMode: "manual",
      selectedPatientId: "",
      patientArchives: [],
      form: {
        assignMode: "random",
        caregiverId: "",
        hospital: "",
        department: "",
        bedNo: "",
        patientName: "",
        patientAge: "",
        gender: "male",
        careLevel: "self",
        startDate: "",
        endDate: "",
      },
      genders: [
        { label: "男", value: "male" },
        { label: "女", value: "female" },
      ],
      careLevels: [
        { label: "能自理", value: "self" },
        { label: "不能自理", value: "full" },
      ],
    };
  },
  computed: {
    submitBarBottom() {
      return "calc(1rem + env(safe-area-inset-bottom))";
    },
    mainBottomPad() {
      return "calc(5.5rem + env(safe-area-inset-bottom))";
    },
    availableDateValues() {
      return this.activeDateOptions.filter((d) => d.available).map((d) => d.date);
    },
    endDateValues() {
      if (!this.form.startDate) return this.availableDateValues;
      return this.availableDateValues.filter((d) => d >= this.form.startDate);
    },
    canPickDates() {
      if (this.form.assignMode === "random") {
        return !!this.form.hospital;
      }
      return !!this.form.caregiverId;
    },
    activeDateOptions() {
      if (this.form.assignMode === "random") {
        return this.buildGenericDates(30);
      }
      return this.dateOptions;
    },
    selectedRangeLabel() {
      if (!this.form.startDate) {
        return "点击选择开始日期，再点击结束日期可连选区间";
      }
      if (!this.form.endDate) {
        return `开始：${this.form.startDate}，请选择结束日期`;
      }
      if (this.form.startDate === this.form.endDate) {
        return `已选 ${this.form.startDate}（再次点击同一天可取消）`;
      }
      return `已选 ${this.form.startDate} 至 ${this.form.endDate}`;
    },
  },
  watch: {
    "form.startDate"(val) {
      if (this.form.endDate && val && this.form.endDate < val) {
        this.form.endDate = val;
      }
    },
  },
  mounted() {
    document.title = "发起陪护 - 安心陪护";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    this.loadHospitals();
    this.loadPatientArchives();
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    async loadPatientArchives() {
      try {
        this.patientArchives = await familyPatientApi.list();
        const defaultOne = this.patientArchives.find((p) => p.isDefault);
        if (defaultOne) {
          this.patientInputMode = "archive";
          this.selectedPatientId = String(defaultOne.id);
          this.applyPatientArchive();
        }
      } catch (e) {
        this.patientArchives = [];
      }
    },
    applyPatientArchive() {
      const p = this.patientArchives.find((item) => String(item.id) === String(this.selectedPatientId));
      if (!p) return;
      this.form.patientName = p.name || "";
      this.form.patientAge = p.age != null ? String(p.age) : "";
      this.form.gender = p.gender || "male";
      this.form.careLevel = p.careLevel === "half" ? "full" : (p.careLevel || "self");
    },
    buildGenericDates(days) {
      const list = [];
      const now = new Date();
      const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      for (let i = 0; i < days; i += 1) {
        const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        list.push({ date: `${y}-${m}-${day}`, available: true, reason: "" });
      }
      return list;
    },
    switchAssignMode(mode) {
      if (mode === "preferred") return;
      if (this.form.assignMode === mode) return;
      this.form.assignMode = mode;
      this.form.caregiverId = "";
      this.form.startDate = "";
      this.form.endDate = "";
      this.dateOptions = [];
      this.detailCaregiver = null;
      this.showCaregiverDetail = false;
    },
    async loadHospitals() {
      try {
        this.hospitalOptions = await familyOrderApi.hospitals();
      } catch (e) {
        this.$message.error((e && e.message) || "加载医院列表失败");
      }
    },
    async onHospitalChange() {
      this.form.caregiverId = "";
      this.form.startDate = "";
      this.form.endDate = "";
      this.dateOptions = [];
      this.detailCaregiver = null;
      this.showCaregiverDetail = false;
      if (!this.form.hospital) {
        this.caregiverOptions = [];
        return;
      }
      if (this.form.assignMode === "random") {
        this.caregiverOptions = [];
        return;
      }
      this.caregiversLoading = true;
      try {
        this.caregiverOptions = await familyOrderApi.caregivers(this.form.hospital);
      } catch (e) {
        this.caregiverOptions = [];
        this.$message.error((e && e.message) || "加载护工列表失败");
      } finally {
        this.caregiversLoading = false;
      }
    },
    async onCaregiverChange() {
      this.form.startDate = "";
      this.form.endDate = "";
      if (!this.form.caregiverId) {
        this.dateOptions = [];
        return;
      }
      this.datesLoading = true;
      try {
        this.dateOptions = await familyOrderApi.caregiverAvailability(this.form.caregiverId, 30);
      } catch (e) {
        this.dateOptions = [];
        this.$message.error((e && e.message) || "加载可预约日期失败");
      } finally {
        this.datesLoading = false;
      }
    },
    resolveUrl(url) {
      if (!url) return "";
      if (url.startsWith("http") || url.startsWith("data:")) return url;
      return url;
    },
    isCaregiverSelected(id) {
      return String(this.form.caregiverId) === String(id);
    },
    openCaregiverDetail(cg) {
      this.detailCaregiver = cg;
      this.showCaregiverDetail = true;
    },
    closeCaregiverDetail() {
      this.showCaregiverDetail = false;
    },
    async selectCaregiver(cg) {
      this.form.caregiverId = String(cg.id);
      this.showCaregiverDetail = false;
      await this.onCaregiverChange();
    },
    dateChipClass(item) {
      if (!item.available) {
        return item.reason === "已有订单" ? "date-chip-busy" : "date-chip-off";
      }
      const inRange = this.isDateInSelectedRange(item.date);
      if (!inRange) {
        return "date-chip-ok";
      }
      const isEdge = item.date === this.form.startDate || item.date === this.form.endDate
        || (!this.form.endDate && item.date === this.form.startDate);
      return isEdge ? "date-chip-selected" : "date-chip-in-range";
    },
    isDateInSelectedRange(date) {
      if (!this.form.startDate) return false;
      const end = this.form.endDate || this.form.startDate;
      return date >= this.form.startDate && date <= end;
    },
    pickDate(item) {
      if (!item.available) {
        this.$message.warning(item.reason === "已有订单" ? "该日期护工已有订单，不可预约" : "该日期护工休息，不可预约");
        return;
      }
      const { date } = item;
      const { startDate, endDate } = this.form;
      if (startDate === date && endDate === date) {
        this.clearDateSelection();
        return;
      }
      if (!startDate) {
        this.form.startDate = date;
        this.form.endDate = "";
        return;
      }
      if (!endDate) {
        if (date < startDate) {
          this.form.startDate = date;
          return;
        }
        if (this.hasBlockedDateBetween(startDate, date)) {
          this.$message.warning("所选区间内包含不可预约日期，请重新选择");
          this.form.startDate = date;
          this.form.endDate = "";
          return;
        }
        this.form.endDate = date;
        return;
      }
      this.form.startDate = date;
      this.form.endDate = "";
    },
    clearDateSelection() {
      this.form.startDate = "";
      this.form.endDate = "";
    },
    hasBlockedDateBetween(start, end) {
      return this.activeDateOptions.some(
        (d) => d.date >= start && d.date <= end && !d.available
      );
    },
    goBack() {
      familyGoBack(this.$router, this.$route, "home");
    },
    validateForm() {
      const f = this.form;
      if (this.patientInputMode === "archive" && !this.selectedPatientId) {
        return false;
      }
      if (!f.hospital || !f.department || !f.bedNo || !f.patientName || !f.patientAge) {
        return false;
      }
      if (f.assignMode === "preferred" && !f.caregiverId) {
        return false;
      }
      if (!f.startDate || !f.endDate) return false;
      if (f.endDate < f.startDate) return false;
      if (this.hasBlockedDateBetween(f.startDate, f.endDate)) return false;
      return true;
    },
    async handleSubmit() {
      if (this.submitting) return;
      if (!this.validateForm()) {
        const msg = this.form.assignMode === "preferred"
          ? "请完整选择医院、意向护工和可预约日期"
          : "请完整填写医院信息与看护日期";
        this.$message.warning(msg);
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          assignMode: this.form.assignMode,
          hospital: this.form.hospital,
          department: this.form.department,
          bedNo: this.form.bedNo,
          patientName: this.form.patientName,
          patientAge: String(this.form.patientAge),
          gender: this.form.gender,
          careLevel: this.form.careLevel,
          startDate: this.form.startDate,
          endDate: this.form.endDate,
        };
        if (this.form.assignMode === "preferred") {
          payload.caregiverId = Number(this.form.caregiverId);
        }
        await familyOrderApi.create(payload);
        this.showSuccess = true;
      } catch (e) {
        const msg = (e && e.message) || "提交失败，请稍后重试";
        this.$message.error(msg);
        if (this.form.assignMode === "preferred" && this.form.caregiverId
            && (msg.indexOf("已有订单") !== -1 || msg.indexOf("休息") !== -1)) {
          await this.onCaregiverChange();
        }
      } finally {
        this.submitting = false;
      }
    },
    closeSuccess() {
      this.showSuccess = false;
    },
    goOrdersAfterSuccess() {
      this.showSuccess = false;
      this.$router.replace({
        path: "/family",
        query: buildFamilyTabQuery("orders", "pending"),
      });
    },
  },
};
</script>

<style scoped>
.family-request {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
}
.family-field {
  margin-top: 0.375rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  background-color: #f1f5f9;
  font-size: 0.875rem;
  color: #1e293b;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.family-field:disabled {
  opacity: 0.6;
}
.family-field::placeholder {
  color: #94a3b8;
}
.family-field:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}
.legend-row {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.legend {
  font-size: 11px;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
}
.legend-ok {
  background: #eff6ff;
  color: #1d4ed8;
}
.legend-busy {
  background: #fef2f2;
  color: #dc2626;
}
.legend-off {
  background: #f1f5f9;
  color: #64748b;
}
.date-range-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.date-range-hint {
  margin: 0;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
  flex: 1;
}
.date-clear-btn {
  flex-shrink: 0;
  font-size: 11px;
  color: #0ea5e9;
  padding: 0.125rem 0.5rem;
  border: 1px solid #bae6fd;
  border-radius: 999px;
  background: #f0f9ff;
}
.date-chip-in-range {
  background: #bae6fd;
  color: #0369a1;
  border-color: #7dd3fc;
}
.date-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.35rem;
  max-height: 220px;
  overflow-y: auto;
}
.date-chip {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.35rem 0.15rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
}
.date-chip-day {
  font-size: 13px;
  font-weight: 600;
}
.date-chip-month {
  font-size: 10px;
  margin-top: 2px;
}
.date-chip-ok {
  background: #eff6ff;
  color: #1d4ed8;
}
.date-chip-busy {
  background: #fef2f2;
  color: #dc2626;
  cursor: not-allowed;
}
.date-chip-off {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}
.date-chip-selected {
  background: #0ea5e9;
  color: #fff;
  border-color: #0284c7;
}
.assign-mode-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
}
.assign-mode-btn {
  border: 2px solid #f1f5f9;
  border-radius: 0.875rem;
  padding: 0.75rem 0.5rem;
  text-align: center;
  background: #fff;
  transition: all 0.2s;
}
.assign-mode-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #f8fafc;
  box-shadow: none;
}
.assign-mode-btn:disabled .assign-mode-title {
  color: #94a3b8;
}
.assign-mode-active {
  border-color: #0ea5e9;
  background: #f0f9ff;
  box-shadow: 0 4px 16px rgb(14 165 233 / 0.15);
}
.assign-mode-icon {
  display: block;
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
}
.assign-mode-title {
  display: block;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #0f172a;
}
.assign-mode-desc {
  display: block;
  font-size: 10px;
  color: #94a3b8;
  margin-top: 0.125rem;
  line-height: 1.3;
}
.random-tip {
  font-size: 12px;
  color: #0369a1;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.75rem;
  padding: 0.75rem;
  line-height: 1.5;
}
.caregiver-scroll {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}
.caregiver-scroll::-webkit-scrollbar {
  display: none;
}
.caregiver-card {
  flex: 0 0 260px;
  scroll-snap-align: start;
  background: #fff;
  border: 2px solid #f1f5f9;
  border-radius: 1rem;
  padding: 0.875rem;
  box-shadow: 0 4px 20px rgb(0 0 0 / 0.05);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.caregiver-card-selected {
  border-color: #0ea5e9;
  box-shadow: 0 4px 20px rgb(14 165 233 / 0.2);
}
.caregiver-avatar-wrap {
  flex-shrink: 0;
  width: 3.5rem;
  height: 3.5rem;
}
.caregiver-photo {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
  object-fit: cover;
}
.caregiver-avatar {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.1);
}
.level-tag {
  flex-shrink: 0;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
}
.skill-tag {
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
  background: #f8fafc;
  font-size: 10px;
  color: #64748b;
}
.selected-hint {
  margin-top: 0.5rem;
  font-size: 11px;
  color: #0ea5e9;
  font-weight: 600;
  text-align: right;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.caregiver-sheet {
  padding-bottom: calc(1.25rem + env(safe-area-inset-bottom));
  max-height: 85vh;
  overflow-y: auto;
}
.sheet-handle {
  width: 2.5rem;
  height: 0.25rem;
  background: #e2e8f0;
  border-radius: 999px;
  margin: 0 auto 1rem;
}
.sheet-avatar {
  width: 4.5rem;
  height: 4.5rem;
}
.sheet-avatar .caregiver-photo,
.sheet-avatar .caregiver-avatar {
  width: 4.5rem;
  height: 4.5rem;
}
.fade-modal-enter-active,
.fade-modal-leave-active {
  transition: opacity 0.28s ease;
}
.fade-modal-enter,
.fade-modal-leave-to {
  opacity: 0;
}
</style>
